<script setup lang="ts">
/* =============================================================
   DOCKING LOCATION — THE OWNER'S BERTH PAGE
   =============================================================
   Everything an owner does to one berth, in four tabs: what it
   is, what it costs, when it is closed, and who has booked it.
   The tab logic is admin/src/pages/BerthDetailPage.vue's: the
   tab lives in ?tab= so a link opens straight on it and Back
   steps between tabs.

   Edit and delete live here and nowhere else, so the decision to
   delete is made looking at the whole record.

   Another company's berth is readable (search needs that) but
   is not yours to manage, so it is "not found" here; its public
   profile is a different page.
   ============================================================= */

import { computed, defineAsyncComponent, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import DetailList from '@/components/ui/DetailList.vue'
import type { DetailRow } from '@/components/ui/DetailList.vue'
import Tabs from '@/components/ui/Tabs.vue'
import type { TabItem } from '@/components/ui/Tabs.vue'
import BerthRatesPanel from '@/components/BerthRatesPanel.vue'
import BerthBlocksPanel from '@/components/BerthBlocksPanel.vue'
import BookingsPanel from '@/components/BookingsPanel.vue'
import type { MapPoint } from '@/lib/map'
import { routes, to } from '@/config/routes'
import { deleteBerth, getBerth } from '@/lib/berths'
import { useCountries } from '@/lib/countries'
import { useAmenities } from '@/lib/amenities'
import { useAuth } from '@/lib/auth/useAuth'
import { bookingModeLabel } from '@/lib/bookingLabels'
import { errorMessage } from '@/lib/errors'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import type { Berth } from '@/lib/types'

const MapView = defineAsyncComponent(() => import('@/components/MapView.vue'))

const route = useRoute()
const router = useRouter()
const { user } = useAuth()
const { countryName } = useCountries()
const { amenityLabel } = useAmenities()

const berth = ref<Berth | null>(null)
/** Filled in by the bookings tab once it has loaded, for the tab's count. */
const bookingCount = ref<number | null>(null)
const loading = ref(true)
const loadError = ref('')

const id = computed(() => Number(route.params.id))

useDocumentTitle(() => berth.value?.name ?? 'Docking location')

/* --- tabs ----------------------------------------------------------------- */

const TAB_KEYS = ['details', 'rates', 'availability', 'bookings'] as const
type TabKey = (typeof TAB_KEYS)[number]

const tab = computed<TabKey>({
  get: () => {
    const q = route.query.tab
    return TAB_KEYS.includes(q as TabKey) ? (q as TabKey) : 'details'
  },
  set: (key) => {
    void router.push({ query: { ...route.query, tab: key === 'details' ? undefined : key } })
  },
})

const tabs = computed<TabItem[]>(() => [
  { key: 'details', label: 'Details' },
  { key: 'rates', label: 'Rates' },
  { key: 'availability', label: 'Availability' },
  { key: 'bookings', label: 'Bookings', count: bookingCount.value },
])

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  bookingCount.value = null
  try {
    const b = await getBerth(id.value)
    berth.value = b && b.company_id === user.value?.companyId ? b : null
  } catch (e) {
    loadError.value = errorMessage(e, 'Could not load the location.')
  } finally {
    loading.value = false
  }
}

onMounted(load)
// Moving between two berth pages reuses this component.
watch(id, load)

const metres = (n: number | null) => (n === null ? null : `${n} m`)

const rows = computed<DetailRow[]>(() => {
  const b = berth.value
  if (!b) return []
  const limit = (label: string, value: string | null): DetailRow =>
    value === null ? { label, empty: true } : { label, value, mono: true }
  return [
    { label: 'City', value: `${b.city}, ${countryName(b.country_code)}` },
    {
      label: 'Booking mode',
      value:
        b.booking_mode === 'manual' && b.response_hours !== null
          ? `${bookingModeLabel(b.booking_mode)} · usually answers within ${b.response_hours} h`
          : bookingModeLabel(b.booking_mode),
    },
    b.amenities.length > 0
      ? { label: 'Amenities', tags: b.amenities.map(amenityLabel) }
      : { label: 'Amenities', empty: true },
    limit('Max length', metres(b.max_length_m)),
    limit('Max width', metres(b.max_width_m)),
    limit('Max draft', metres(b.max_draft_m)),
    limit('Vessels at once', b.max_vessels === null ? null : String(b.max_vessels)),
    { label: 'Position', value: `${b.latitude.toFixed(5)}, ${b.longitude.toFixed(5)}`, mono: true },
  ]
})

const points = computed<MapPoint[]>(() =>
  berth.value
    ? [{ id: berth.value.id, lat: berth.value.latitude, lng: berth.value.longitude, title: berth.value.name }]
    : [],
)

/* --- delete --------------------------------------------------------------- */

const confirming = ref(false)
const confirm = useTemplateRef<InstanceType<typeof ConfirmDialog>>('confirm')

async function onDeleteConfirmed(): Promise<void> {
  const b = berth.value
  if (!b) return
  try {
    await deleteBerth(b.id)
    confirming.value = false
    await router.push(routes.locations)
  } catch (e) {
    confirm.value?.fail(errorMessage(e, 'Could not delete the location.'))
  }
}
</script>

<template>
  <p v-if="loading" class="page-status">Loading…</p>

  <template v-else-if="berth">
    <PageHeader :title="berth.name" :description="`${berth.city}, ${countryName(berth.country_code)}`">
      <template #actions>
        <Button variant="ghost" icon="chevronRight" :to="routes.locations" class="back-link">
          All locations
        </Button>
        <Button variant="secondary" icon="edit" :to="to.berthEdit(berth.id)">Edit</Button>
        <Button variant="danger" icon="trash" @click="confirming = true">Delete</Button>
      </template>
    </PageHeader>

    <Tabs v-model="tab" :tabs="tabs" label="Location sections" />

    <div :id="`tabpanel-${tab}`" role="tabpanel" :aria-labelledby="`tab-${tab}`">
      <div v-if="tab === 'details'" class="details">
        <Panel>
          <DetailList :rows="rows" />
        </Panel>
        <div class="mapWrap">
          <MapView :points="points" height="100%" />
        </div>
      </div>

      <BerthRatesPanel v-else-if="tab === 'rates'" :berth-id="berth.id" />

      <BerthBlocksPanel v-else-if="tab === 'availability'" :berth-id="berth.id" />

      <BookingsPanel
        v-else
        :filter="{ berthId: berth.id }"
        :hide="['berth']"
        description="At this berth, newest arrival first."
        @loaded="bookingCount = $event"
      />
    </div>

    <ConfirmDialog
      ref="confirm"
      :open="confirming"
      title="Delete location"
      :message="`“${berth.name}” will be removed with its rates and blocked time. This cannot be undone.`"
      @close="confirming = false"
      @confirmed="onDeleteConfirmed"
    />
  </template>

  <template v-else>
    <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>
    <EmptyState
      v-else
      icon="anchor"
      title="Location not found"
      description="It may have been deleted, or it is not one of yours."
    >
      <template #action>
        <Button :to="routes.locations">Your locations</Button>
      </template>
    </EmptyState>
  </template>
</template>

<style scoped>
.details {
  display: grid;
  gap: var(--space-md);
  grid-template-columns: minmax(0, 1fr);
}

.mapWrap {
  height: 16rem;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

@media (min-width: 64rem) {
  .details {
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    align-items: start;
  }

  .mapWrap {
    height: 24rem;
  }
}
</style>
